import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import gql from 'graphql-tag';
import * as Sentry from '@sentry/nextjs';
import { Box, Typography, Button, Chip, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import EventIcon from '@mui/icons-material/Event';
import ArticleIcon from '@mui/icons-material/Article';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NextLink from 'next/link';
import { getImageUrl } from '../utils/utils';
import moment from 'moment';

const GET_FEATURED = gql`
  query FeaturedContentBanner {
    featuredContents {
      id
      contentType
      contentId
      position
      event {
        id
        label
        shortDescription
        startedAt
        endedAt
        city
        url
        pictures {
          id
          originalPicturePath
          originalPictureFilename
          main
        }
      }
      article {
        id
        label
        shortDescription
        url
        pictures {
          id
          originalPicturePath
          originalPictureFilename
          main
        }
      }
      petiteAnnonce {
        id
        title
        shortDescription
        city
        type
        url
        pictures {
          id
          originalPicturePath
          originalPictureFilename
          main
        }
      }
      recipe {
        id
        label
        shortDescription
        url
        pictures {
          id
          originalPicturePath
          originalPictureFilename
          main
        }
      }
    }
  }
`;

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    width: '100%',
    background: 'linear-gradient(135deg, #2C367E 0%, #d96552 100%)',
    padding: '2em 0',
    position: 'relative',
  },
  viewport: {
    overflow: 'hidden',
    width: '100%',
    touchAction: 'pan-y',
  },
  track: {
    display: 'flex',
    width: '100%',
    willChange: 'transform',
  },
  slide: {
    flex: '0 0 100%',
    minWidth: 0,
  },
  inner: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '0 1.5em',
    display: 'flex',
    alignItems: 'center',
    gap: '2em',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: '1em',
    },
  },
  imageBox: {
    flexShrink: 0,
    width: 180,
    height: 130,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: 180,
    },
  },
  textBox: {
    flex: 1,
    color: 'white',
  },
  label: {
    fontSize: '1.4em',
    fontWeight: 'bold',
    lineHeight: 1.3,
    marginBottom: '0.4em',
    [theme.breakpoints.down('md')]: { fontSize: '1.1em' },
  },
  description: {
    fontSize: '0.95em',
    opacity: 0.9,
    marginBottom: '0.8em',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  chip: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    marginBottom: '0.6em',
    '& .MuiChip-icon': { color: 'white' },
  },
  btn: {
    backgroundColor: 'white',
    color: '#2C367E',
    fontWeight: 'bold',
    borderRadius: '2em',
    padding: '0.4em 1.8em',
    '&:hover': { backgroundColor: '#fcc100', color: '#2C367E' },
  },
  meta: {
    fontSize: '0.85em',
    opacity: 0.8,
    marginBottom: '0.5em',
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.15)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
    zIndex: 2,
  },
  navBtnLeft: { left: 8 },
  navBtnRight: { right: 8 },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: 8,
    marginTop: '1em',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.4)',
    cursor: 'pointer',
    border: 'none',
    padding: 0,
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.7)' },
  },
  dotActive: { backgroundColor: 'white' },
}));

const formatEventDate = (
  startedAt: string | number | null | undefined,
  city: string | null | undefined,
): string | null => {
  if (!startedAt) return null;
  try {
    const ts = typeof startedAt === 'number' ? startedAt : parseInt(String(startedAt), 10);
    if (!Number.isFinite(ts)) return null;
    const m = moment(ts).locale('fr');
    if (!m.isValid()) return null;
    return `Le ${m.format('dddd D MMMM YYYY')}${city ? ` · ${city}` : ''}`;
  } catch (e) {
    return null;
  }
};

type Normalized = {
  key: string;
  label: string;
  shortDescription: string | null;
  href: string;
  imageUrl: string | null;
  chipLabel: string;
  chipIcon: React.ReactElement;
  buttonLabel: string;
  meta: string | null;
};

const normalize = (item: any): Normalized | null => {
  if (!item || typeof item !== 'object') return null;

  const pickImage = (content: any): string | null => {
    const pictures = Array.isArray(content?.pictures) ? content.pictures : [];
    const main = pictures.find((p: any) => p && p.main) || pictures[0] || null;
    const raw = main && main.originalPicturePath ? main.originalPicturePath : null;
    return raw ? getImageUrl(raw) : null;
  };

  switch (item.contentType) {
    case 'event': {
      const c = item.event;
      if (!c || !c.label) return null;
      return {
        key: `event-${item.id}`,
        label: c.label,
        shortDescription: c.shortDescription || null,
        href: `/evenement/${c.url || c.id}`,
        imageUrl: pickImage(c),
        chipLabel: '★ Événement à la une',
        chipIcon: <EventIcon />,
        buttonLabel: "Voir l'événement",
        meta: formatEventDate(c.startedAt, c.city),
      };
    }
    case 'article': {
      const c = item.article;
      if (!c || !c.label) return null;
      return {
        key: `article-${item.id}`,
        label: c.label,
        shortDescription: c.shortDescription || null,
        href: `/article/${c.url || c.id}`,
        imageUrl: pickImage(c),
        chipLabel: '★ Article à la une',
        chipIcon: <ArticleIcon />,
        buttonLabel: "Lire l'article",
        meta: null,
      };
    }
    case 'petite_annonce': {
      const c = item.petiteAnnonce;
      if (!c || !c.title) return null;
      return {
        key: `petite-annonce-${item.id}`,
        label: c.title,
        shortDescription: c.shortDescription || null,
        href: `/petites-annonces/${c.url || c.id}`,
        imageUrl: pickImage(c),
        chipLabel: '★ Petite annonce à la une',
        chipIcon: <LocalOfferIcon />,
        buttonLabel: "Voir l'annonce",
        meta: c.city || null,
      };
    }
    case 'recipe': {
      const c = item.recipe;
      if (!c || !c.label) return null;
      return {
        key: `recipe-${item.id}`,
        label: c.label,
        shortDescription: c.shortDescription || null,
        href: `/recette/${c.url || c.id}`,
        imageUrl: pickImage(c),
        chipLabel: '★ Recette à la une',
        chipIcon: <RestaurantIcon />,
        buttonLabel: 'Voir la recette',
        meta: null,
      };
    }
    default:
      return null;
  }
};

const Slide = ({ item }: { item: Normalized }) => {
  const { classes } = useStyles();
  const [imgError, setImgError] = useState(false);

  return (
    <Box className={classes.inner}>
      {item.imageUrl && !imgError && (
        <Box className={classes.imageBox}>
          <img
            src={item.imageUrl}
            alt={item.label}
            onError={() => setImgError(true)}
            draggable={false}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
          />
        </Box>
      )}
      <Box className={classes.textBox}>
        <Chip icon={item.chipIcon} label={item.chipLabel} size="small" className={classes.chip} />
        <Typography className={classes.label}>{item.label}</Typography>
        {item.meta && <Typography className={classes.meta}>{item.meta}</Typography>}
        {item.shortDescription && (
          <Typography className={classes.description}>{item.shortDescription}</Typography>
        )}
        <Button
          component={NextLink}
          href={item.href}
          className={classes.btn}
          variant="contained"
          disableElevation
        >
          {item.buttonLabel}
        </Button>
      </Box>
    </Box>
  );
};

const FeaturedBannerInner = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const { data, error } = useQuery(GET_FEATURED, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !mounted,
    ssr: false,
  });
  const { classes, cx } = useStyles();
  const [index, setIndex] = useState(0);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const dragStart = useRef<{ x: number; y: number; locked: 'h' | 'v' | null } | null>(null);

  // Ref callback : se déclenche quand le DOM node est réellement monté
  // (les early-returns plus bas empêchent un useEffect classique de fonctionner
  // car le node n'existe pas tant que les data ne sont pas chargées).
  const setViewportRef = useCallback((el: HTMLDivElement | null) => {
    viewportRef.current = el;
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    if (el) {
      setViewportWidth(el.clientWidth);
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(() => {
          if (viewportRef.current) setViewportWidth(viewportRef.current.clientWidth);
        });
        ro.observe(el);
        resizeObserverRef.current = ro;
      }
    }
  }, []);

  useEffect(() => () => {
    if (resizeObserverRef.current) resizeObserverRef.current.disconnect();
  }, []);

  if (!mounted || error) return null;

  const list: any[] = data && Array.isArray((data as any).featuredContents)
    ? (data as any).featuredContents
    : [];
  const items = list.map(normalize).filter((i): i is Normalized => i !== null);

  if (items.length === 0) return null;

  const safeIndex = Math.min(index, items.length - 1);
  const hasMany = items.length > 1;

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(items.length - 1, i + 1));

  // 5% du viewport pour valider le swipe
  const commitThreshold = Math.max(20, viewportWidth * 0.05);

  // Translation de la track en pixels
  const baseTranslate = -safeIndex * viewportWidth;
  // Empêche d'aller au-delà des extrémités (effet "résistance" diviseur 3)
  let effectiveDrag = dragOffset;
  if (safeIndex === 0 && effectiveDrag > 0) effectiveDrag = effectiveDrag / 3;
  if (safeIndex === items.length - 1 && effectiveDrag < 0) effectiveDrag = effectiveDrag / 3;
  const translateX = baseTranslate + effectiveDrag;

  const onTouchStart = (e: React.TouchEvent) => {
    if (!hasMany) return;
    const t = e.touches[0];
    dragStart.current = { x: t.clientX, y: t.clientY, locked: null };
    setIsDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragStart.current) return;
    const t = e.touches[0];
    const dx = t.clientX - dragStart.current.x;
    const dy = t.clientY - dragStart.current.y;
    // détection de la direction au premier mouvement significatif
    if (dragStart.current.locked === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      dragStart.current.locked = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
    }
    if (dragStart.current.locked === 'h') {
      setDragOffset(dx);
    }
  };

  const onTouchEnd = () => {
    if (!dragStart.current) return;
    const locked = dragStart.current.locked;
    const offset = dragOffset;
    dragStart.current = null;
    setIsDragging(false);
    setDragOffset(0);
    if (locked !== 'h') return;
    if (offset <= -commitThreshold) next();
    else if (offset >= commitThreshold) prev();
  };

  return (
    <Box className={classes.wrapper}>
      {hasMany && (
        <IconButton
          aria-label="Précédent"
          onClick={prev}
          className={cx(classes.navBtn, classes.navBtnLeft)}
          size="small"
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      <Box
        ref={setViewportRef}
        className={classes.viewport}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        <Box
          className={classes.track}
          sx={{
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: isDragging ? 'none' : 'transform 300ms cubic-bezier(0.22, 0.61, 0.36, 1)',
          }}
        >
          {items.map((it) => (
            <Box key={it.key} className={classes.slide}>
              <Slide item={it} />
            </Box>
          ))}
        </Box>
      </Box>

      {hasMany && (
        <IconButton
          aria-label="Suivant"
          onClick={next}
          className={cx(classes.navBtn, classes.navBtnRight)}
          size="small"
        >
          <ChevronRightIcon />
        </IconButton>
      )}
      {hasMany && (
        <Box className={classes.dots}>
          {items.map((it, i) => (
            <button
              key={it.key}
              type="button"
              aria-label={`Item ${i + 1}`}
              onClick={() => setIndex(i)}
              className={cx(classes.dot, i === safeIndex && classes.dotActive)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const FeaturedBanner = () => (
  <Sentry.ErrorBoundary fallback={<></>}>
    <FeaturedBannerInner />
  </Sentry.ErrorBoundary>
);

export default FeaturedBanner;
